<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from "vue";
import { useRoute } from "vue-router";

import SkoreSpinner from "@/components/ui/SkoreSpinner.vue";
import FullPageCenteredLayout from "@/layouts/FullPageCenteredLayout.vue";
import { useApi } from "@/services/api";

type State = "loading" | "form" | "success" | "error";

const OTP_LENGTH = 6;

const api = useApi();
const route = useRoute();
const input = useTemplateRef<HTMLInputElement>("input");
const uiState = ref<State>("form");
const otp = ref("");
const otpChars = computed(() => {
  const a = Array.from(otp.value);
  while (a.length < OTP_LENGTH) {
    a.push("");
  }
  return a;
});

function focus() {
  input.value?.focus();
}

async function onKeyUp() {
  if (otp.value?.length === OTP_LENGTH) {
    const { state } = route.query;
    if (state) {
      uiState.value = "loading";
      const isSuccess = await api.deviceCallback(state as string, otp.value);
      uiState.value = isSuccess ? "success" : "error";
    }
  }
}

onMounted(() => {
  window.addEventListener("click", focus);
  focus();
});

onBeforeUnmount(() => {
  window.removeEventListener("click", focus);
});
</script>

<template>
  <FullPageCenteredLayout>
    <main class="device-token">
      <div class="wrapper">
        <SkoreSpinner v-if="uiState === 'loading'" />
        <div class="otp-form" v-else-if="uiState === 'form'">
          <label for="otp">
            Please confirm this action by entering the code provided by your Skore client.
          </label>
          <input
            name="otp"
            ref="input"
            type="text"
            autocomplete="one-time-code"
            inputmode="text"
            :maxlength="OTP_LENGTH"
            :pattern="'(?:\d|\D){' + OTP_LENGTH + '}'"
            v-model="otp"
            @keyup="onKeyUp"
          />
          <div class="otp-chars">
            <div
              class="otp-char"
              v-for="(c, i) in otpChars"
              :key="i"
              :class="{ filled: c !== '', next: otp.length === i }"
            >
              {{ c }}
            </div>
          </div>
        </div>
        <div class="success" v-if="uiState === 'success'">
          Successfully authenticated you may now close this window.
        </div>
        <div class="error" v-if="uiState === 'error'">Something went wrong please retry.</div>
      </div>
    </main>
  </FullPageCenteredLayout>
</template>

<style scoped>
.device-token {
  display: flex;
  width: 100dvw;
  height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .wrapper {
    .otp-form {
      display: flex;
      flex-direction: column;
      align-items: center;

      .otp-chars {
        display: flex;
        flex-direction: row;
        padding-top: var(--numbers-24);
        gap: var(--numbers-24);

        .otp-char {
          width: var(--numbers-56);
          height: var(--numbers-56);
          align-content: center;
          border: var(--stroke-width-md) solid var(--border-tertiary);
          font-family: GeistMono, monospace;
          font-size: var(--numbers-18);
          text-align: center;
          transition: border-color var(--animation-duration) var(--animation-easing);

          &.filled {
            border-color: var(--border-secondary);
          }

          &.next::after {
            animation: cursor 0.6s ease-in infinite;
            animation-direction: alternate;
            color: inherit;
            content: "|";
          }
        }
      }

      input {
        position: absolute;
        width: 0;
        opacity: 0;
      }
    }

    .success {
      color: var(--content-positive);
    }

    .error {
      color: var(--content-negative);
    }
  }
}

@keyframes cursor {
  0% {
    color: transparent;
  }

  70%,
  100% {
    color: currentcolor;
  }
}
</style>
